import { useState, useEffect } from "react";
import { useHistory } from "react-router-dom";
import {
  deleteUserService,
  getAllUserListService,
} from "../../services/api-service";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { Trash2, Edit2, Users, Loader, Calendar } from "lucide-react";

const DeleteUser = () => {
  const history = useHistory();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const [deleting, setDeleting] = useState(false);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    setLoading(true);
    try {
      const response = await getAllUserListService();
      setUsers(response?.data || []);
    } catch (error) {
      toast.error("Failed to load users");
    } finally {
      setLoading(false);
    }
  };

  const handleModify = (user) => {
    history.push("add_user", { user: user, modify: true });
  };

  const handleDelete = async (email) => {
    setDeleting(true);
    try {
      const response = await deleteUserService({ email });
      if (response?.status === 202) {
        toast.success("User deleted successfully");
        setDeleteConfirm(null);
        fetchData();
        setTimeout(() => window.location.assign("/"), 1500);
      }
    } catch (error) {
      toast.error("Failed to delete user");
    } finally {
      setDeleting(false);
    }
  };

  const getRoleDisplay = (user) => {
    if (user.is_admin && user.is_owner) return { text: "Admin & Owner", color: "#9333ea" };
    if (user.is_admin) return { text: "Admin", color: "#f97316" };
    if (user.is_owner) return { text: "Owner", color: "#eab308" };
    return { text: "User", color: "#6b7280" };
  };

  const canModify = (user) => ("is_owner" in user && !user.is_owner) || !("is_owner" in user);

  // Helper function to format the date
  const formatDate = (dateString) => {
    if (!dateString) return "N/A";
    const date = new Date(dateString);
    return date.toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  };

  const styles = {
    container: {
      minHeight: "100vh",
      background: "linear-gradient(135deg, #f8f9fa 0%, #ffffff 50%, #f0f4f8 100%)",
      padding: "24px",
      fontFamily: "'Segoe UI', 'Helvetica Neue', sans-serif",
    },
    maxWidth: {
      maxWidth: "1400px",
      margin: "0 auto",
    },
    header: {
      marginBottom: "32px",
    },
    headerTop: {
      display: "flex",
      alignItems: "center",
      gap: "12px",
      marginBottom: "8px",
    },
    headerIcon: {
      padding: "8px",
      background: "#dbeafe",
      borderRadius: "8px",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      color: "#0284c7",
    },
    headerTitle: {
      fontSize: "32px",
      fontWeight: 700,
      color: "#1a202c",
      margin: 0,
    },
    headerSubtitle: {
      fontSize: "14px",
      color: "#64748b",
      margin: "4px 0 0 40px",
    },
    tableWrapper: {
      background: "white",
      borderRadius: "12px",
      boxShadow: "0 4px 6px rgba(0, 0, 0, 0.07), 0 10px 20px rgba(0, 0, 0, 0.05)",
      overflow: "hidden",
      border: "1px solid #e2e8f0",
    },
    table: {
      width: "100%",
      borderCollapse: "collapse",
      fontSize: "14px",
    },
    thead: {
      background: "linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%)",
      borderBottom: "2px solid #e2e8f0",
    },
    th: {
      padding: "16px",
      textAlign: "left",
      fontWeight: 700,
      color: "#334155",
      letterSpacing: "0.3px",
      textTransform: "uppercase",
      fontSize: "12px",
    },
    tbody: {
      display: "tableRowGroup",
    },
    tr: {
      borderBottom: "1px solid #e2e8f0",
      transition: "all 0.2s ease",
    },
    td: {
      padding: "16px",
      color: "#1a202c",
    },
    roleCell: {
      display: "inline-flex",
      alignItems: "center",
      gap: "6px",
      padding: "6px 12px",
      borderRadius: "6px",
      fontSize: "12px",
      fontWeight: 600,
      whiteSpace: "nowrap",
    },
    dateCell: {
      display: "flex",
      alignItems: "center",
      gap: "6px",
      color: "#64748b",
      fontSize: "13px",
    },
    actionsCell: {
      display: "flex",
      gap: "8px",
    },
    btnModify: {
      display: "inline-flex",
      alignItems: "center",
      gap: "6px",
      padding: "8px 14px",
      background: "#dbeafe",
      color: "#0284c7",
      border: "none",
      borderRadius: "6px",
      fontSize: "13px",
      fontWeight: 600,
      cursor: "pointer",
      transition: "all 0.2s ease",
    },
    btnModifyHover: {
      background: "#0284c7",
      color: "white",
    },
    btnDelete: {
      display: "inline-flex",
      alignItems: "center",
      gap: "6px",
      padding: "8px 14px",
      background: "#fee2e2",
      color: "#dc2626",
      border: "none",
      borderRadius: "6px",
      fontSize: "13px",
      fontWeight: 600,
      cursor: "pointer",
      transition: "all 0.2s ease",
    },
    btnDeleteHover: {
      background: "#dc2626",
      color: "white",
    },
    emptyState: {
      textAlign: "center",
      padding: "60px 24px",
    },
    emptyIcon: {
      width: "64px",
      height: "64px",
      margin: "0 auto 16px",
      opacity: 0.3,
      color: "#94a3b8",
    },
    emptyText: {
      fontSize: "16px",
      color: "#64748b",
      fontWeight: 500,
    },
    loadingContainer: {
      display: "flex",
      flexDirection: "column",
      alignItems: "center",
      justifyContent: "center",
      minHeight: "400px",
      gap: "16px",
    },
    spinner: {
      animation: "spin 1s linear infinite",
    },
    loadingText: {
      color: "#64748b",
      fontSize: "14px",
    },
    modal: {
      position: "fixed",
      inset: 0,
      background: "rgba(0, 0, 0, 0.5)",
      backdropFilter: "blur(4px)",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      zIndex: 50,
      padding: "16px",
    },
    modalContent: {
      background: "white",
      borderRadius: "12px",
      boxShadow: "0 20px 50px rgba(0, 0, 0, 0.15)",
      maxWidth: "400px",
      width: "100%",
      padding: "32px 24px",
      animation: "slideUp 0.3s ease",
    },
    modalIcon: {
      width: "48px",
      height: "48px",
      background: "#fee2e2",
      borderRadius: "50%",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      margin: "0 auto 16px",
      color: "#dc2626",
    },
    modalTitle: {
      fontSize: "20px",
      fontWeight: 700,
      color: "#1a202c",
      margin: "0 0 8px 0",
      textAlign: "center",
    },
    modalEmail: {
      fontSize: "14px",
      fontWeight: 600,
      color: "#1a202c",
      margin: "8px 0 16px 0",
      textAlign: "center",
    },
    modalDescription: {
      fontSize: "14px",
      color: "#64748b",
      margin: "0 0 8px 0",
      textAlign: "center",
      lineHeight: 1.5,
    },
    modalWarning: {
      fontSize: "12px",
      color: "#64748b",
      margin: "0 0 24px 0",
      textAlign: "center",
      fontStyle: "italic",
    },
    modalActions: {
      display: "flex",
      gap: "12px",
    },
    btnCancel: {
      flex: 1,
      padding: "10px 16px",
      background: "white",
      color: "#64748b",
      border: "1px solid #e2e8f0",
      borderRadius: "6px",
      fontSize: "14px",
      fontWeight: 600,
      cursor: "pointer",
      transition: "all 0.2s ease",
    },
    btnCancelHover: {
      background: "#f8fafc",
      borderColor: "#cbd5e1",
    },
    btnConfirmDelete: {
      flex: 1,
      padding: "10px 16px",
      background: "#dc2626",
      color: "white",
      border: "none",
      borderRadius: "6px",
      fontSize: "14px",
      fontWeight: 600,
      cursor: "pointer",
      transition: "all 0.2s ease",
    },
    btnConfirmDeleteHover: {
      background: "#b91c1c",
    },
  };

  return (
    <>
      <style>{`
        @keyframes spin {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }
        @keyframes slideUp {
          from { opacity: 0; transform: translateY(20px); }
          to { opacity: 1; transform: translateY(0); }
        }
        table tr:hover {
          background-color: #f8fafc;
        }
      `}</style>

      <div style={styles.container}>
        <div style={styles.maxWidth}>
          {/* Header */}
          <div style={styles.header}>
            <div style={styles.headerTop}>
              <div style={styles.headerIcon}>
                <Users size={24} />
              </div>
              <h1 style={styles.headerTitle}>Team Members</h1>
            </div>
            <p style={styles.headerSubtitle}>Manage and organize your team members</p>
          </div>

          {/* Table */}
          <div style={styles.tableWrapper}>
            {loading ? (
              <div style={styles.loadingContainer}>
                <Loader size={40} style={styles.spinner} color="#0284c7" />
                <p style={styles.loadingText}>Loading team members...</p>
              </div>
            ) : users.length === 0 ? (
              <div style={styles.emptyState}>
                <Users size={64} style={styles.emptyIcon} />
                <p style={styles.emptyText}>No team members yet</p>
              </div>
            ) : (
              <table style={styles.table}>
                <thead style={styles.thead}>
                  <tr style={styles.tr}>
                    <th style={styles.th}>Role</th>
                    <th style={styles.th}>Name</th>
                    <th style={styles.th}>Email</th>
                    <th style={styles.th}>Date Created</th> {/* ✅ Added New Column Header */}
                    <th style={{ ...styles.th, width: "200px" }}>Actions</th>
                  </tr>
                </thead>
                <tbody style={styles.tbody}>
                  {users.map((user) => {
                    const role = getRoleDisplay(user);
                    const isModifiable = canModify(user);

                    return (
                      <tr key={user.email} style={styles.tr}>
                        <td style={styles.td}>
                          <div
                            style={{
                              ...styles.roleCell,
                              background: `${role.color}15`,
                              color: role.color,
                            }}
                          >
                            {role.text}
                          </div>
                        </td>
                        <td style={styles.td}>
                          <span style={{ fontWeight: 500 }}>{user.name}</span>
                        </td>
                        <td style={styles.td}>
                          <span style={{ color: "#64748b", fontSize: "13px" }}>
                            {user.email}
                          </span>
                        </td>
                        
                        {/* ✅ Added New Date Data Cell */}
                        <td style={styles.td}>
                          <div style={styles.dateCell}>
                            <Calendar size={14} />
                            <span>{formatDate(user.date)}</span>
                          </div>
                        </td>

                        <td style={styles.td}>
                          {isModifiable ? (
                            <div style={styles.actionsCell}>
                              <button
                                style={styles.btnModify}
                                onClick={() => handleModify(user)}
                                onMouseEnter={(e) =>
                                  Object.assign(e.target.style, styles.btnModifyHover)
                                }
                                onMouseLeave={(e) => {
                                  e.target.style.background = "#dbeafe";
                                  e.target.style.color = "#0284c7";
                                }}
                              >
                                <Edit2 size={14} />
                                Edit
                              </button>
                              <button
                                style={styles.btnDelete}
                                onClick={() => setDeleteConfirm(user)}
                                onMouseEnter={(e) =>
                                  Object.assign(e.target.style, styles.btnDeleteHover)
                                }
                                onMouseLeave={(e) => {
                                  e.target.style.background = "#fee2e2";
                                  e.target.style.color = "#dc2626";
                                }}
                              >
                                <Trash2 size={14} />
                                Delete
                              </button>
                            </div>
                          ) : (
                            <span
                              style={{
                                fontSize: "12px",
                                color: "#94a3b8",
                                fontWeight: 500,
                              }}
                            >
                              Protected
                            </span>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            )}
          </div>
        </div>
      </div>

      {/* Delete Confirmation Modal */}
      {deleteConfirm && (
        <div style={styles.modal}>
          <div style={styles.modalContent}>
            <div style={styles.modalIcon}>
              <Trash2 size={24} />
            </div>
            <h3 style={styles.modalTitle}>Delete User?</h3>
            <p style={styles.modalDescription}>Are you sure you want to delete</p>
            <p style={styles.modalEmail}>{deleteConfirm.email}</p>
            <p style={styles.modalWarning}>This action cannot be undone.</p>
            <div style={styles.modalActions}>
              <button
                style={styles.btnCancel}
                onClick={() => setDeleteConfirm(null)}
                disabled={deleting}
                onMouseEnter={(e) =>
                  !deleting && Object.assign(e.target.style, styles.btnCancelHover)
                }
                onMouseLeave={(e) => {
                  e.target.style.background = "white";
                  e.target.style.borderColor = "#e2e8f0";
                }}
              >
                Cancel
              </button>
              <button
                style={{
                  ...styles.btnConfirmDelete,
                  opacity: deleting ? 0.7 : 1,
                  cursor: deleting ? "not-allowed" : "pointer",
                }}
                onClick={() => handleDelete(deleteConfirm.email)}
                disabled={deleting}
                onMouseEnter={(e) =>
                  !deleting && Object.assign(e.target.style, styles.btnConfirmDeleteHover)
                }
                onMouseLeave={(e) => {
                  e.target.style.background = "#dc2626";
                }}
              >
                {deleting ? "Deleting..." : "Delete"}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default DeleteUser;
